import React, { useEffect, useRef, useState } from "react";
import GenericCard from "components/UI/GenericCard/GenericCard";
import { apiGetPerformanceStats } from "api/apiTrades";
import { useSelector } from "react-redux";
import { Button, Divider, Select } from "antd";

import SymbolCheckup from "./SymbolCheckup";
import OnlineUsers from "./OnlineUsers";
import NasdaqStatus from "./NasdaqStatus";
import TVChartContainer from "components/TVChartContainer/TVChartContainer";
import RecentTrades from "components/Home/RecentTrades";
import WinRateOverTime from "./WinRateOverTime";
import PerformanceStatsCards from "./PerformanceStatsCards";
import PortfolioProfitOverTime from "./PortfolioProfitOverTime";
import RecentMonthsPNL from "./RecentMonthsPNL";
import { ReloadOutlined } from "@ant-design/icons";
import {
  PERFORMANCE_PERIODS,
  findPerformancePeriod,
  periodStartDate,
} from "utils/performancePeriods";

const PerformanceStats = () => {
  const [performanceStats, setPerformanceStats] = useState(null);
  const accountType = useSelector((state) => state.main.accountType);
  const [loading, setLoading] = useState(false);
  // the closed-trade stats and charts below follow it
  const [period, setPeriod] = useState(PERFORMANCE_PERIODS[0]);

  // a late answer for the previous period/account must not land
  const runId = useRef(0);

  const getPerformanceStats = async (isInitialLoad = false) => {
    const thisRun = ++runId.current;
    try {
      if (!isInitialLoad) {
        setLoading(true);
      }
      const response = await apiGetPerformanceStats(
        accountType,
        undefined,
        periodStartDate(period),
      );
      if (thisRun === runId.current) setPerformanceStats(response);
    } catch (error) {
      console.error(error);
    } finally {
      if (!isInitialLoad) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // no old numbers under the new period's label (also if the fetch fails)
    setPerformanceStats(null);
    getPerformanceStats(true);
  }, [accountType, period]);

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Divider orientation="left">Portfolio Performance</Divider>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={period.value}
            onChange={(value) => setPeriod(findPerformancePeriod(value))}
            options={PERFORMANCE_PERIODS.map(({ value, label }) => ({
              value,
              label,
            }))}
            style={{ width: 140 }}
          />
          <Button
            shape="circle"
            icon={<ReloadOutlined />}
            onClick={() => getPerformanceStats(false)}
            loading={loading}
          />
        </div>
      </div>
      <div className="space-y-4 pb-6">
        <PerformanceStatsCards
          performanceStats={performanceStats}
          periodLabel={period.label}
        />

        <div className="content-grid">
          <div className="flex flex-col gap-4">
            <SymbolCheckup />
            <OnlineUsers />
            <NasdaqStatus />
          </div>
          <RecentMonthsPNL performanceStats={performanceStats} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[2.8fr_1.5fr] gap-6">
          <PortfolioProfitOverTime
            pnlOverTime={performanceStats?.pnlOverTime}
          />
          <RecentTrades trades={performanceStats?.lastTrades} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-6">
          <GenericCard className="table-card [&>.content]:p-0">
            <TVChartContainer />
          </GenericCard>
          <WinRateOverTime
            winRateOverTime={performanceStats?.winRateOverTime}
          />
        </div>
      </div>
    </>
  );
};

export default PerformanceStats;
